import React, { useState, useRef } from 'react';
import { uploadAPI } from '../services/api';

const UploadStep = ({ photos, setPhotos, onNext, onBack, uploading, setUploading }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  const handleFiles = (files) => {
    // Only allow JPG and PNG files
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const imageFiles = files.filter(file => allowedTypes.includes(file.type.toLowerCase()));

    // Check if any files were filtered out due to unsupported format
    if (files.length > imageFiles.length) {
      alert('Only JPG and PNG files are allowed');
      return;
    }
    const file = imageFiles[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        // Replace any existing photos with the new one
        setPhotos([{
          id: Date.now() + Math.random(),
          url: e.target.result,
          file: file
        }]);
      };
      reader.readAsDataURL(file);
    if (imageFiles.length === 1) {
      
    }
  };

  const handleRemovePhoto = (id) => {
    setPhotos(prev => prev.filter(photo => photo.id !== id));
  };



  const handleNext = async () => {
    if (photos.length === 0) {
      return;
    }

    setUploading(true);

    try {
      // Upload each photo to S3 and confirm
      const uploadedPhotos = await Promise.all(
        photos.map(async (photo) => {
          try {
            // Request presigned URL from backend with image_type='photo'
            const response = await uploadAPI.getPresignedUrl(
              photo.file.name,
              photo.file.type,
              'photo' // User photos go to user-specific folder
            );

            const { presigned_url, file_key } = response.data;

            // Upload file to S3 using presigned URL
            await uploadAPI.uploadToS3(presigned_url, photo.file);

            // Confirm upload to save metadata in database
            const confirmResponse = await uploadAPI.confirmUpload(
              file_key,
              photo.file.name,
              photo.file.type,
              'photo' // Mark as user photo
            );

            return {
              ...photo,
              fileKey: file_key,
              uploadUUID: confirmResponse.data.upload_uuid,
              uploaded: true,
            };
          } catch (error) {
            console.error('Error uploading photo:', error);
            throw new Error(`Failed to upload ${photo.file.name}`);
          }
        })
      );

      // Update photos with file keys and UUIDs
      setPhotos(uploadedPhotos);

      // Proceed to next step
      onNext();
    } catch (error) {
      alert(error.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-section">
      <div className="step-header-with-nav">
        <div className="navigation-buttons">
          <button className="btn-back" onClick={onBack}>
            ← Back
          </button>
        </div>
        <h2 style={{ fontSize: '24px', margin: '0' }}>
          Upload Your Photo
        </h2>
        <div className="navigation-buttons">
          <button 
            className="btn-next" 
            onClick={handleNext}
            disabled={photos.length === 0 || uploading}
          >
            {uploading ? 'Uploading...' : 'Next: Personalize →'}
          </button>
        </div>
      </div>
      {/* Hidden file input used by both upload area and upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {photos.length === 0 && (
        <p style={{ textAlign: 'center', color: '#718096', marginBottom: '30px' }}>
          Upload one clear photo (JPG or PNG format only)
        </p>
      )}

      {photos.length === 0 && (
        <div
          className={`upload-area ${dragOver ? 'dragover' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onTouchStart={() => {}} // Enable touch interactions on mobile
        >
          <div className="upload-icon">📤</div>
          <h3>Drop your photo here</h3>
          <p>or tap to browse from your device</p>
          <button className="upload-btn" type="button">
            🖼️ Choose Photo
          </button>
        </div>
      )}

      {photos.length > 0 && (
        <div className="preview-section">
          <h3 style={{ marginBottom: '15px' }}>Uploaded Photo</h3>
          <div style={{ display: 'inline', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
            <div className="preview-grid">
              {photos.map(photo => (
                <div key={photo.id} className="preview-item">
                  <img src={photo.url} alt="Preview" />
                  <button
                    className="preview-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto(photo.id);
                    }}
                    type="button"
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button 
              className="upload-btn" 
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              🖼️ Upload Photo
            </button>
          </div>
        </div>
      )}

      {/* <div className="sample-photos">
        <p>Or try with sample photos</p>
        <div className="sample-options">
          <button className="sample-btn" onClick={() => handleSampleClick('Solo')}>
            👤 Solo
          </button>
          <button className="sample-btn" onClick={() => handleSampleClick('Couple')}>
            👥 Couple
          </button>
          <button className="sample-btn" onClick={() => handleSampleClick('Family')}>
            👨‍👩‍👧 Family (3)
          </button>
        </div>
      </div> */}


      <div className="photo-tips">
        <div className="tips-header">
          ✅ Best Photo Tips:
        </div>
        <ul className="tips-list">
          <li>Use clear, well-lit photos with visible faces</li>
          <li>Front-facing or slightly angled works best</li>
          <li>Only JPG and PNG formats are supported</li>
          <li>Upload one photo at a time</li>
        </ul>
      </div>

      <div className="ai-notice">
        <div className="ai-notice-icon">ℹ️</div>
        <p>
          Our AI will automatically detect each person in your photo and fit the t-shirt design
          perfectly on everyone!
        </p>
      </div>
    </div>
  );
};

export default UploadStep;
